import React, { Fragment } from 'react'
import Helmet from 'react-helmet'
import {
  Absolute,
  Inset,
  Break,
  Flex,
  Stack,
  Space,
  On,
} from 'spaced-components'
import { NavbarThemeSwitch } from '../../components/NavbarTheme'
import WhenVisible from '../../components/WhenVisible'
import BackgroundText from '../../components/BackgroundText'
import VideoWrapper from '../../components/VideoWrapper'
import Parallax from '../../components/Parallax'
import { AutoFade, AutoSlide } from '../../components/Animated'
import Img from '../../components/Img'

import CaseStudyLayout, {
  Title,
  Box,
  Detail,
  DetailValue,
  Paragraph,
  InfoSection,
} from '../CaseStudyLayout'

import { getProject } from '../../projects'

import devices from './assets/devices.png'
import city from './assets/city.png'
import ipad from './assets/ipad.png'
import webpage from './assets/webpage.png'
import img01 from './assets/img01.png'
import img02 from './assets/img02.png'
import img03 from './assets/img03.png'
import video from './assets/video.mp4'

import Logo from './Logo'

const currentProject = getProject('ac-hotels')

export default () => (
  <Fragment>
    <Helmet>
      <title>{currentProject.title}</title>
      <meta name="description" content={currentProject.summary} />
    </Helmet>
    <NavbarThemeSwitch theme="dark" />

    <CaseStudyLayout currentProjectKey={currentProject.key} logo={<Logo />}>
      <Box css={{ backgroundColor: '#c6c4b9' }}>
        <Inset x={{ mobile: 'lg', tabletMd: 0 }}>
          <InfoSection
            text={
              <Fragment>
                <Title is="h1" css={{ maxWidth: '14em' }}>
                  Revitalizing Wilshire Blvd for&nbsp;the modern entrepreneur
                </Title>
                <Space lg />
                <Paragraph
                  css={{
                    width: { mobile: '100%', tabletMd: '95%', laptop: '90%' },
                  }}
                >
                  We helped our agency partner bring Marriott’s newest brand, AC
                  Hotel to historic Wilshire Blvd. With the recent growth and
                  development of design-forward lifestyle hotels in the Los
                  Angeles market, we knew we had to intelligently showcase how
                  AC Hotels would benefit the community. Such as energizing and
                  bringing much-needed tax revenue for the area. The campaign
                  focused on the brand's key positioning – a conveniently
                  located, upscale business hotel designed with the modern
                  entrepreneur in mind. We built a microsite featuring an
                  interactive 360º viewer giving the audience a sneak peek of
                  hotel's guest rooms and amenities encapsulating AC’s mission
                  “European-inspired classic modernism.”
                </Paragraph>
              </Fragment>
            }
            details={
              <Stack is="ul" rowGap={15}>
                <li>
                  <Detail muted>Client:</Detail>
                  <Break />
                  <Space xs />
                  <DetailValue>AC Hotels</DetailValue>
                </li>
                <li>
                  <Detail muted>Agency:</Detail>
                  <Break />
                  <Space xs />
                  <DetailValue>Consensus Inc.</DetailValue>
                </li>
                <li>
                  <Detail muted>Industry:</Detail>
                  <Break />
                  <Space xs />
                  <DetailValue>Hospitality</DetailValue>
                </li>
                <li>
                  <Detail muted>Services:</Detail>
                  <Break />
                  <Space xs />
                  <DetailValue>
                    Creative Direction
                    <Break />
                    Website Design
                  </DetailValue>
                </li>
              </Stack>
            }
          />
          <AutoSlide up>
            <Box css={{ width: { tablet: '75%' }, margin: 'auto' }}>
              <Img
                src={devices}
                ratio={821 / 1380}
                style={{ marginBottom: '-40vmin', marginLeft: '-7%' }}
              />
            </Box>
          </AutoSlide>
        </Inset>
      </Box>
      <Box css={{ backgroundColor: '#0a0a0a' }}>
        <Space y="40vmin" />
        <Space xl />
        <Box
          css={{
            width: { mobile: '80%', tabletMd: '66.666%' },
            margin: 'auto',
          }}
        >
          <WhenVisible>
            {inView => (
              <Fragment>
                <On min="tabletSm">
                  <Space x={{ tabletSm: '-5.55vmax' }}>
                    <BackgroundText
                      is="h4"
                      animate={inView}
                      style={{ textAlign: 'left' }}
                    >
                      DESIGN
                    </BackgroundText>
                  </Space>
                </On>
                <Space y={{ tabletSm: '-7vw' }} />
              </Fragment>
            )}
          </WhenVisible>
        </Box>
        <Box
          css={{
            width: { mobile: '80%', tabletMd: '66.666%' },
            margin: 'auto',
          }}
        >
          <WhenVisible>
            {inView => (
              <Box
                css={{
                  display: 'flex',
                  justifyContent: 'center',
                  position: 'relative',
                  transition: '1s ease-out',
                  opacity: inView ? 1 : 0,
                }}
              >
                <Box css={{ width: '85%', position: 'relative', zIndex: 1 }}>
                  <Img src={img01} ratio={493 / 897} />
                </Box>
                <Box
                  css={{
                    width: '65%',
                    position: 'absolute',
                    left: '-10%',
                    top: '9%',
                    transition: '1s .5s ease-out',
                    transform: inView ? '' : 'translateX(30%)',
                  }}
                >
                  <Img src={img02} ratio={385 / 701} />
                </Box>
                <Box
                  css={{
                    width: '65%',
                    position: 'absolute',
                    right: '-10%',
                    top: '9%',
                    transition: '1s .5s ease-out',
                    transform: inView ? '' : 'translateX(-30%)',
                  }}
                >
                  <Img src={img03} ratio={385 / 701} />
                </Box>
              </Box>
            )}
          </WhenVisible>
        </Box>
        <Space xl tabletMd="xxl" />
        <Box
          css={{
            width: { mobile: '80%', tabletMd: '66.666%' },
            margin: 'auto',
          }}
        >
          <AutoFade>
            <Img src={webpage} ratio={3528 / 1134} />
          </AutoFade>
        </Box>
        <Space xl tabletMd="xxl" />
      </Box>
      <Box
        css={{
          width: { mobile: '80%', tabletMd: '66.666%' },
          margin: 'auto',
        }}
      >
        <WhenVisible>
          {inView => (
            <Fragment>
              <On min="tabletSm">
                <Space x={{ tabletSm: '-5.55vmax' }}>
                  <BackgroundText
                    is="h4"
                    animate={inView}
                    style={{ textAlign: 'left' }}
                  >
                    360º VIEWER
                  </BackgroundText>
                </Space>
              </On>
              <Space y={{ tabletSm: '-7vw' }} />
            </Fragment>
          )}
        </WhenVisible>
      </Box>
      <Box
        css={{
          padding: '0 16.666% 20vw',
          background: `url(${city}) center / cover #0a0a0a`,
        }}
      >
        <AutoSlide up style={{ position: 'relative' }}>
          <Img src={ipad} ratio={740 / 1019} />
          <Absolute width="78%" top="13.8%" left="10.5%">
            <VideoWrapper ratio={248 / 371}>
              <video autoPlay loop playsInline muted>
                <source src={video} type="video/mp4" />
              </video>
            </VideoWrapper>
          </Absolute>
        </AutoSlide>
      </Box>
      <Parallax direction="both" height="70vh" maxHeight={600}>
        <Parallax.Background height="150%">
          <Box css={{ height: '100%', backgroundColor: '#c6c4b9' }}>
            <Box
              css={{
                width: { mobile: '80%', tabletLg: '45%' },
                position: 'relative',
                top: { mobile: '60%', tabletLg: '35%' },
                left: '-9%',
              }}
            >
              <svg viewBox="0 0 46.9 46.9" fill="#bfbdb2">
                <path d="M46.2 37.9v8.3H.8v-8.3h45.4m.7-.8H0v9.8h46.9v-9.8zM46.2.8v36.4H.8V.8h45.4m.7-.8H0v37.9h46.9V0zM40 11l-1.2.8-.1-.2a6.3 6.3 0 0 0-4.4-3.2c-2.2-.5-4.5 0-6.3 1.4a6 6 0 0 0-2.3 3.7 6.6 6.6 0 0 0 5.1 8.1c1.5.3 3 .2 4.4-.2a6.7 6.7 0 0 0 3.6-2.8c.3-.5.3-.5.9-.3l.7.4-.1.2a8.4 8.4 0 0 1-5.4 4 12 12 0 0 1-3.8.2c-1.2-.1-2.3-.5-3.4-1-1.8-1-3-2.7-3.5-4.6-.6-1.8-.4-3.8.4-5.6a7.5 7.5 0 0 1 4.3-4.1A8.3 8.3 0 0 1 37 8c1.3.7 2.3 1.7 3 3m-20.7 6.4l-4.4-8.7-4.3 8.7h8.7zm4.1 5.2h-1.5a.2.2 0 0 1-.2-.2L20 19a.5.5 0 0 0-.5-.3h-9.2c-.2 0-.4.1-.4.3l-1.7 3.5-.3.2H6.6l.3-.7 3.6-7.4 3.4-6.9c.1-.2.3-.3.5-.3h1.1l.3.2 2.9 5.9 4.6 9 .1.1m-6.6 6.1c0-.9-.6-1.6-1.5-1.6h-.2c-.9-.1-1.7.6-1.7 1.5-.1.9.6 1.7 1.5 1.7h.3c.8.1 1.6-.5 1.7-1.4l-.1-.2m-1.6 2.1c-.8.1-1.5-.2-2-.8-.7-.9-.5-2.3.4-3 .2-.2.4-.3.7-.3.8-.3 1.7-.2 2.4.3 1 .7 1.2 2 .5 3-.5.5-1.2.9-2 .8m13.6-1.9h-2.3c-.5 0-.5 0-.5.5v.7h3c.3 0 .3.1.3.3s0 .3-.3.3h-3.6v-4H29v.5h-3v1.1h2.5c.4 0 .4 0 .4.4l-.1.2m-18.8 0H7.5c-.3 0-.3 0-.3.3v1.4c0 .2-.1.3-.3.3h-.3v-4.1h.6l.1.2v1.2c0 .2.1.3.3.3h2.2c.2 0 .3 0 .3-.3v-1.4h.7v3.7c0 .2-.1.3-.3.3H10v-1.9zm26.5.4l.6-.1c0 .4.3.7.6.9.5.2 1 .2 1.5 0 .3-.1.5-.3.4-.6 0-.3-.2-.5-.5-.5l-1.6-.4c-.7-.1-.9-.5-.9-.9 0-.5.3-.9.8-1.1a3 3 0 0 1 1.9 0c.5.2.8.6.8 1.1h-.5l-.1-.1c-.1-.4-.4-.6-.8-.7a2 2 0 0 0-1.1.1l-.3.4c0 .2.1.3.3.4l1.1.3.9.2c.4.1.6.4.6.8.1.4-.1.9-.5 1.1-.8.4-1.8.4-2.7 0-.2 0-.5-.4-.5-.9m-17-2.1v-.5h3.8v.5h-1.5V30.4c0 .2-.1.3-.3.3h-.1c-.2 0-.3 0-.3-.3v-3c0-.2-.1-.3-.3-.3l-1.3.1m11.7-.6h.6v3.1c0 .5 0 .4.4.4h2.2v.1c.1.5.1.5-.5.5h-2.8l.1-4.1zM18 41.2v.8l.1.2c.3.1.6.1.9-.1.4-.5.3-1.2-.1-1.6l-.6-.2-.2.2-.1.7m4.7 1c.3.1.7-.2.7-.5v-.8c-.1-.4-.4-.7-.8-.7-.3 0-.3.1-.3.3v1.2c-.1.4-.1.5.4.5M21.1 40h2.1c.6 0 1 .5 1 1l-.2.6-.4.5v.3l1 1.2c.2.3.5.5.9.5.3 0 .4-.1.4-.3v-3.2c0-.3-.1-.5-.5-.5l.1-.1h1.6c-.4.1-.4.3-.4.6V43.8c0 .2.1.4.3.4h.1l.2.1h-2.1c-.5 0-1-.2-1.2-.6l-.7-1c-.2-.2-.4-.3-.7-.2l-.1.2v1.1c0 .3.1.4.4.4l.1.1h-2.1c-.5 0-.9-.2-1.2-.6l-.6-.8c-.1-.3-.5-.5-.8-.3V44l.2.2.2.1h-1.5c.4-.1.4-.4.4-.7v-3c0-.4 0-.4-.4-.5l-.1-.1H17h1.4l1 .2c.5.2.7.8.5 1.2l-.5.7-.2.2 1.3 1.6c.2.2.5.3.7.3s.4 0 .4-.3v-.6-2.5c0-.5 0-.5-.5-.7m-6.3 2.4l-.5-1.4h-.1l-.5 1.4h1.1zm-.4-2.7l.3.7 1.2 2.9c.2.5.3.6.8.8h-1.3l-.3-1c-.2-.6-.1-.5-.7-.5-.8 0-.8 0-1.1.7l-.1.2c-.1.3-.1.4.3.5h-2.4l-.4-2.8h-.1l-1.4 2.8-1.4-2.7-.1.5-.2 1.7c0 .3 0 .4.3.4l.1.1H6.6c.7-.2.5-.8.6-1.3l.5-2.9.1-.3.2.3 1.3 2.8.2.3 1.6-3.3h.1l.1.6.5 3.2c0 .2.1.4.4.5.3.1.5-.1.6-.3l1-2.5.4-1.1.2-.3m17.1 2.4l-.2-.9c-.2-.8-1-1.2-1.7-1.1-.6.1-1 .5-1.1 1.1-.2.8 0 1.7.6 2.3.5.5 1.3.6 1.9.1l.4-.7.1-.8m.8-.2c0 1.2-.9 2.3-2.2 2.3H30c-1.2 0-2.2-1-2.2-2.2s1-2.2 2.2-2.2c1.1-.2 2.1.6 2.3 1.7v.4m4-1.3c-.1-.4-.4-.4-.7-.5h-.2c-.6 0-.6 0-.6.6v2.6c0 .6 0 .6.5.8h-1.6c.4-.1.4-.4.4-.7v-3c0-.2-.1-.3-.3-.3h-.5c-.2 0-.6 0-.8.4l.1-.7h3.6l.1.8zm3.1 3.5h-1.7c.4-.1.4-.4.4-.7v-3c0-.2-.1-.3-.3-.3h-.3c-.4 0-.8-.1-1 .4h-.1l.1-.7h3.6v.8c-.1-.4-.5-.4-.8-.4h-.6l-.1.7v2.5c.3.4.3.5.8.7" />
              </svg>
            </Box>
          </Box>
        </Parallax.Background>
        <Parallax.Content>
          <Flex height="100%" center middle>
            <Inset x={{ mobile: 'lg', tabletMd: 0 }}>
              <Title
                is="blockquote"
                css={{ maxWidth: '21em', textAlign: 'center' }}
              >
                Our work on the AC Hotel proposal helped activate members of the
                neighborhood to show support for the project that would
                revitalize Wilshire Blvd.
              </Title>
            </Inset>
          </Flex>
        </Parallax.Content>
      </Parallax>
    </CaseStudyLayout>
  </Fragment>
)
